import React, { createContext, useContext, useState, useEffect } from "react";

const AUTH_KEY = "hse_auth_session_v3";
const CRED_KEY = "hse_admin_credentials_v3";
const LEGACY_CRED_KEY = "hse_admin_credentials_disabled";

// Frontend-only demo credentials. For a production client, replace this with backend auth.
const DEFAULT_USER = "mwyadmin";
const DEFAULT_PASS = "MWY@2026";
const DEFAULT_DISPLAY_NAME = "Muhammad Waqas Younas";

const AuthContext = createContext(null);

async function sha256(text) {
  const msgBuffer = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function cleanCredsForStorage(creds) {
  const { legacyPassword, ...safe } = creds || {};
  return safe;
}

function loadCreds() {
  try {
    const raw = localStorage.getItem(CRED_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.username && parsed?.passwordHash) return parsed;
    }
  } catch {}

  // Backward compatibility with the previous localStorage credential format.
  try {
    const raw = localStorage.getItem(LEGACY_CRED_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.username && parsed?.password) {
        return {
          username: parsed.username,
          legacyPassword: parsed.password,
          passwordHash: null,
          displayName: parsed.displayName || DEFAULT_DISPLAY_NAME,
          role: "Administrator",
          mustChangePassword: true,
        };
      }
    }
  } catch {}

  return {
    username: DEFAULT_USER,
    legacyPassword: DEFAULT_PASS,
    passwordHash: null,
    displayName: DEFAULT_DISPLAY_NAME,
    role: "Administrator",
    mustChangePassword: true,
  };
}

export function AuthProvider({ children }) {
  const [creds, setCreds] = useState(loadCreds);

  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  });

  useEffect(() => {
    try {
      if (session) localStorage.setItem(AUTH_KEY, JSON.stringify(session));
      else localStorage.removeItem(AUTH_KEY);
    } catch {}
  }, [session]);

  useEffect(() => {
    try { localStorage.setItem(CRED_KEY, JSON.stringify(cleanCredsForStorage(creds))); } catch {}
  }, [creds]);

  const isPasswordValid = async (password) => {
    const passwordHash = await sha256(password);
    if (creds.passwordHash && passwordHash === creds.passwordHash) return { ok: true, passwordHash };
    if (creds.legacyPassword && password === creds.legacyPassword) return { ok: true, passwordHash, legacy: true };
    return { ok: false, passwordHash };
  };

  const login = async (username, password) => {
    if (username !== creds.username) return { ok: false, error: "Invalid username or password" };

    const valid = await isPasswordValid(password);
    if (!valid.ok) return { ok: false, error: "Invalid username or password" };

    const nextCreds = valid.legacy
      ? { ...creds, passwordHash: valid.passwordHash, legacyPassword: undefined, mustChangePassword: true }
      : creds;

    if (valid.legacy) setCreds(nextCreds);

    setSession({
      user: {
        username: nextCreds.username,
        name: nextCreds.displayName || DEFAULT_DISPLAY_NAME,
        role: nextCreds.role || "Administrator",
        mustChangePassword: !!nextCreds.mustChangePassword,
      },
      loggedAt: new Date().toISOString(),
    });
    return { ok: true };
  };

  const logout = () => setSession(null);

  const updateCredentials = async ({ currentPassword, newUsername, newPassword, displayName }) => {
    const valid = await isPasswordValid(currentPassword);
    if (!valid.ok) return { ok: false, error: "Current password is incorrect" };

    const nextPasswordHash = newPassword ? await sha256(newPassword) : (creds.passwordHash || valid.passwordHash);
    const next = {
      username: newUsername?.trim() || creds.username,
      passwordHash: nextPasswordHash,
      displayName: displayName?.trim() || creds.displayName || DEFAULT_DISPLAY_NAME,
      role: creds.role || "Administrator",
      mustChangePassword: false,
    };
    setCreds(next);
    try { localStorage.removeItem(LEGACY_CRED_KEY); } catch {}

    if (session) {
      setSession({
        ...session,
        user: {
          ...session.user,
          username: next.username,
          name: next.displayName,
          role: next.role,
          mustChangePassword: false,
        },
      });
    }
    return { ok: true };
  };

  return (
    <AuthContext.Provider value={{ session, login, logout, updateCredentials, creds: cleanCredsForStorage(creds), isAuthed: !!session }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
